import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withRouter } from "react-router-dom";
import classNames from "classnames";
import Nav from "reactstrap/lib/Nav";
import Navbar from "reactstrap/lib/Navbar";
import NavbarBrand from "reactstrap/lib/NavbarBrand";
import NavbarToggler from "reactstrap/lib/NavbarToggler";
import Collapse from "reactstrap/lib/Collapse";
import debounce from "lodash/debounce";
import selectn from "selectn";
import * as navigationActions from "../../../actions/navigation";
import Logo from "../Logo";
import Icon from "../Icon";
import ProfileIcon from "../ProfileIcon";
import LatestStories from "../LatestStories";
import SearchWindow from "./SearchWindow";
import Overlay from "../Overlay/Overlay";
import NavigationDropdown from "./NavigationDropdown";
import { BREAKPOINT_DOWN_MEDIUM } from "../../../config/constants";
import * as authDrawerActions from "../../../actions/authDrawer";
import * as authDrawerConstants from "../../../constants/authDrawer";
import { freezeScroll, thawScroll } from "../../../utils/toggleScroll";

import styles from "./Navigation.css";
import globalStyles from "../../../styles/global.css";

class Navigation extends Component {
  constructor(props) {
    super(props);

    this.toggle = this.toggle.bind(this);
    this.toggleLatest = this.toggleLatest.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.openAuthDrawer = this.openAuthDrawer.bind(this);
    this.toggleSearchWindow = this.toggleSearchWindow.bind(this);

    this.state = {
      isOpen: false,
      isLatestOpen: false,
      navigationScrolled: false,
      isMobile: false,
      searchWindowOpen: false,
    };
  }

  componentDidMount() {
    this.mediaQuery = window.matchMedia(BREAKPOINT_DOWN_MEDIUM);

    this.setState({ isMobile: this.mediaQuery.matches });

    if (!this.props.navigation.results.sections.length) this.props.navigationActions.getSections();
    if (!this.props.navigation.results.specialFeatures.length)  this.props.navigationActions.getSpecialFeatures();

    this.mediaQuery.addListener(this.handleResize);
    this.scrollHandler = debounce(this.handleScroll.bind(this), 20);
    window.addEventListener("scroll", this.scrollHandler);
  }

  componentWillUnmount() {
    this.mediaQuery.removeListener(this.handleResize);
    window.removeEventListener("scroll", this.scrollHandler);
  }

  componentDidUpdate(prevProps) {
    // If URL changes, close the search window
    if (this.props.location !== prevProps.location) {
      this.setState({ searchWindowOpen: false, isOpen: false, isLatestOpen: false });
    }
  }

  handleScroll() {
    if (window.scrollY > 110 && !this.state.navigationScrolled) {
      this.setState({
        navigationScrolled: true,
      });
    } else if (window.scrollY < 110 && this.state.navigationScrolled) {
      this.setState({
        navigationScrolled: false,
      });
    }
  }

  handleResize(event) {
    this.setState({ isMobile: event.matches });
  }

  toggle() {
    this.setState({
      isOpen: !this.state.isOpen,
      searchWindowOpen: false,
      isLatestOpen: false,
    });
  }

  toggleLatest() {
    this.setState({
      isLatestOpen: !this.state.isLatestOpen,
      searchWindowOpen: false,
      isOpen: false,
    });
  }

  openAuthDrawer(step) {
    this.setState({
      isLatestOpen: false,
      searchWindowOpen: false,
      isOpen: false,
    });
    this.props.authDrawerActions.toggleAuthDrawer(true);
    this.props.authDrawerActions.changeStep(step);
  }

  toggleSearchWindow() {
    this.setState({
      searchWindowOpen: !this.state.searchWindowOpen,
      isLatestOpen: false,
      isOpen: false,
    });
  }

  getMoreItems() {
    return [
      { name: "Newsletters", url: "/newsletters", internal: true },
      { name: "Events", url: "/events", internal: true },
      { name: "About", url: "/about", internal: true },
      { name: "Contact Newsroom", url: "mailto:news@axios.com" },
      { name: "Search", onClick: this.toggleSearchWindow, className: "gtm-search-more-menu" },
      { name: "Privacy & Terms", url: "/legal", internal: true },
      { name: "Advertise With Us", url: "mailto:ads@axios.com" },
    ];
  }

  getAccountItems() {
    if (this.props.isLoggedIn) {
      return [
        { name: "Saved Stories", url: "/users/saved-stories", internal: true, className: "gtm-saved-stories" },
        { name: "Your Account", url: "/users/settings", internal: true, className: "gtm-your-account" },
        { name: "Log Out", onClick: () => this.props.authDrawerActions.logOut().then(() => this.props.history.push("/")), className: "gtm-log-out" },
      ];
    }
    return [
      { name: "Sign In", onClick: () => this.openAuthDrawer(authDrawerConstants.SIGNIN), className: "gtm-sign-in" },
      { name: "Create Account", onClick: () => this.openAuthDrawer(authDrawerConstants.REGISTER1), className: "gtm-create-account" },
    ];
  }

  render() {
    const { searchWindowOpen, isLatestOpen, isOpen } = this.state;
    if (searchWindowOpen || isLatestOpen || isOpen) {
      freezeScroll();
    } else {
      thawScroll();
    }

    return (
      <div>
        <Navbar
          role="navigation"
          fixed="top"
          className={classNames(styles.root, styles.fixed, this.state.navigationScrolled ? styles.compressed : {}, this.state.isOpen || this.state.isLatestOpen ? styles.open : {})}
          toggleable="md">
          <a href="#main" className={classNames(globalStyles.a11yOnly, styles.item)}>
            Skip to content
          </a>
          <NavbarToggler
            left
            onClick={this.toggle}
            className={classNames(styles.left, "gtm-hamburger-menu", this.state.isOpen ? styles.togglerOpen : styles.toggler)}
            role="button"
            aria-haspopup="true"
            aria-expanded={this.state.isOpen}
            aria-hidden={!this.state.isMobile}
            alt="Toggle main menu">
            <Icon name="hamburger" description="Mobile toggle main menu" path="img" className={styles.icon}/>
          </NavbarToggler>
          <NavbarBrand className={styles.brand} href="/">
            <div className={styles.logoContainer}>
              {
                this.props.section && this.state.navigationScrolled && this.mediaQuery.matches ?
                  <div className={styles.aOnWhite}>
                    <span className={styles.tag}>{this.props.section.name}</span>
                  </div> :
                  <Logo isCompressed={this.state.navigationScrolled}/>
              }
              <h1 className={globalStyles.a11yOnly}>Axios</h1>
            </div>
          </NavbarBrand>
          <NavbarToggler
            right
            alt="Sidebar toggle"
            onClick={this.toggleLatest}
            className={classNames(styles.right, "gtm-timer-icon", this.state.isLatestOpen ? styles.togglerOpen : styles.toggler)}>
            <Icon name="timer" description="Mobile toggle complementary sections" className={classNames(styles.icon, styles.timerIcon)} />
          </NavbarToggler>
          <Collapse
            navbar
            id="main-menu"
            isOpen={this.state.isOpen}
            role={this.state.isMobile ? "menu" : "none"}
            className={this.state.isOpen && styles.menuOpen}>
            <Nav className="mr-auto" navbar>
              <NavigationDropdown
                name="Sections"
                isMobile={this.state.isMobile}
                mobileToggle={this.toggle}
                items={this.props.navigation.results.sections}
                className="gtm-menu-dropdown"/>
              <NavigationDropdown
                name="Special Features"
                isMobile={this.state.isMobile}
                mobileToggle={this.toggle}
                items={[...(this.props.navigation.results.specialFeatures || [])]}
                className="gtm-menu-dropdown"/>
              <NavigationDropdown
                name="More"
                isMobile={this.state.isMobile}
                mobileToggle={this.toggle}
                items={this.getMoreItems()}
                className="gtm-menu-dropdown"/>
            </Nav>
            <Nav navbar className="ml-auto">
              <NavigationDropdown
                name="Account"
                toggle={<ProfileIcon/>}
                isMobile={this.state.isMobile}
                mobileToggle={this.toggle}
                items={this.getAccountItems()}/>
              {
                !this.state.isMobile &&
                <div className={classNames(styles.searchIcon, "gtm-search-icon")}>
                  <Icon
                    name="search"
                    description="Search"
                    path="img"
                    onClick={this.toggleSearchWindow}
                    className={classNames("mr-1", styles.icon)}/>
                </div>
              }
            </Nav>
          </Collapse>
          { this.state.isLatestOpen &&
            <div
              role="complementary"
              className={classNames(styles.menuOpen, styles.sidebar)}
            >
              <LatestStories/>
            </div>
          }
        </Navbar>

        { this.state.searchWindowOpen ?
          <div>
            <SearchWindow isCompressed={this.state.navigationScrolled} onHideSearch={this.toggleSearchWindow} />
            <Overlay onClick={this.toggleSearchWindow} />
          </div>
          : null }
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    navigation: state.navigation,
    section: selectn("navigation.data.section", state),
    authDrawer: state.authDrawer,
    isLoggedIn: !!state.authDrawer.authToken, // TODO: implement this properly when singup etc is done
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    navigationActions: bindActionCreators(navigationActions, dispatch),
    authDrawerActions: bindActionCreators(authDrawerActions, dispatch),
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Navigation));
